import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import AgoraVideoCall from "./AgoraVideoCall";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AgoraRTMTranscriptions from "./AgoraRTMTranscriptions";

function VideoCallPage() {
  const [callData, setCallData] = useState(null);
  const [localTracks, setLocalTracks] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [liveTranslation, setLiveTranslation] = useState(false); // Live Translation state
  const [showSubTitle, setShowSubTitle] = useState(false); // Show Subtitle Of Translation
  const [messages, setMessages] = useState([]); // Messages Of Translation

  const getUserData = localStorage.getItem("userInfo");

  const [yourPreferredLanguage, setYourPreferredLanguage] = useState(
    JSON.parse(getUserData)?.preferredLang || ""
  ); // Set the initial value of yourPreferredLanguage
  const [selectedDevicePreferredLanguage, setSelectedDevicePreferredLanguage] =
    useState(callData?.preferredLanguage || ""); // Set the initial value from props of devicePreferredLanguage
  
  useEffect(() => {
    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encodedData = searchParams.get("data");
    const callType = searchParams.get("type");
    if (encodedData) {
      try {
        const decodedData = JSON.parse(atob(encodedData));
        setCallData({
          ...decodedData,
          isEventCall: callType === "event",
          isGroupCall: callType === "group",
        });

        initLocalTracks(
          decodedData.initialCameraEnabled,
          decodedData.initialMicrophoneEnabled,
          decodedData.initialAudio,
          decodedData.initialVideo
        );
        setSelectedDevicePreferredLanguage(
          decodedData.devicePreferredLang || ""
        );
        setYourPreferredLanguage(
          JSON.parse(getUserData)?.preferredLang || ""
        );
       
      } catch (error) {
        console.error("Failed to decode call data:", error);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }

    return () => {
      if (localTracks) {
        localTracks.audioTrack.stop();
        localTracks.audioTrack.close();
        localTracks.videoTrack.stop();
        localTracks.videoTrack.close();
      }
    };
  }, [location]);

  const initLocalTracks = async (
  cameraEnabled,
  microphoneEnabled,
  audioId,
  videoId
) => {
  try {
    console.log('🔧 Initializing tracks with parameters:', {
      audioId: audioId || 'default',
      videoId: videoId || 'default',
      cameraEnabled,
      microphoneEnabled,
      timestamp: new Date().toISOString()
    });

    if (isMobile) {
      try {
        const existingStreams = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        existingStreams.getTracks().forEach((track) => track.stop());
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (err) {
        console.error("Failed to get media permissions:", err);
        throw err;
      }
    }

    let audioTrack;
    let videoTrack;

    if (isMobile) {
      audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: "music_standard",
        microphoneId: audioId || undefined,
      });

      videoTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: { width: 640, height: 360, frameRate: 15 },
        facingMode: "user",
      });

      audioTrack.on("track-ended", () => {
        console.log("mobile audio track ended");
      });
      videoTrack.on("track-ended", () => {
        console.log("mobile video track ended");
      });

      console.log("Mobile tracks created:", { audioTrack, videoTrack });

      if (!audioTrack) {
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      }
      if (!videoTrack) {
        videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: { width: 640, height: 360, frameRate: 15 },
        });
      }
    } else {
      let effectiveAudioId = audioId;
      
      if (!audioId || audioId === "") {
        const devices = await AgoraRTC.getMicrophones();
        const defaultDevice = devices.find(d => d.deviceId === "default");
        
        if (defaultDevice) {
          console.log('Using explicit default audio device:', defaultDevice.label);
          effectiveAudioId = "default";
        } else {
          console.log('No explicit default device found, letting browser choose');
          effectiveAudioId = undefined;
        }
      }

      if (effectiveAudioId) {
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: effectiveAudioId,
        });
      } else {
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      }

      let effectiveVideoId = videoId;
      
      if (!videoId || videoId === "") {
        const devices = await AgoraRTC.getCameras();
        const defaultDevice = devices.find(d => d.deviceId === "default");
        
        if (defaultDevice) {
          // console.log('Using explicit default camera device:', defaultDevice.label);
          effectiveVideoId = "default";
        } else {
          // console.log('No explicit default camera found, letting browser choose');
          effectiveVideoId = undefined;
        }
      }

      if (effectiveVideoId) {
        videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: { width: 640, height: 360, frameRate: 15 },
          cameraId: effectiveVideoId,
        });
      } else {
        videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: { width: 640, height: 360, frameRate: 15 },
        });
      }
    }

    await audioTrack.setEnabled(microphoneEnabled);
    await videoTrack.setEnabled(cameraEnabled);

    setLocalTracks({ audioTrack, videoTrack });
    console.log("Tracks initialized and enabled:", {
      audio: microphoneEnabled,
      video: cameraEnabled,
    });
    setIsLoading(false);
  } catch (error) {
    console.error("Failed to create local tracks:", error);
    setIsLoading(false);
  }
};

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="black"
      >
        <CircularProgress color="primary" size={60} thickness={4} />
        <Typography color="primary" style={{ marginTop: "20px" }}>
          Initializing video call...
        </Typography>
      </Box>
    );
  }

  if (!callData || !localTracks) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="black"
        color="white"
        fontSize="1.2rem"
      >
        Failed to initialize video call. Please try again.
      </Box>
    );
  }

  return (
      <div style={{ width: "100vw", height: "100vh", backgroundColor: "black" }}>
      <AgoraVideoCall
        userInfo={callData.userInfo}
        callingDeviceId={callData.callingDeviceId}
        callingAccountId={callData.callingAccountId}
        callerName={callData.callerName}
        policyId={callData.policyId}
        isGroupCall={callData.isGroupCall}
        eventId={callData.eventId}
        isEventCall={callData.isEventCall}
        isJoiningEvent={callData.isJoiningEvent}
        onClose={() => window.close()}
        localVideoTrack={localTracks.videoTrack}
        localAudioTrack={localTracks.audioTrack}
        initialCameraEnabled={callData.initialCameraEnabled}
        initialMicrophoneEnabled={callData.initialMicrophoneEnabled}
        audiosource={callData.initialAudio}
        videosource={callData.initialVideo}
        audioOutput={callData.initialAudioOutput}
        answerName={callData.answerName}
        setLiveTranslation={setLiveTranslation}
        liveTranslation={liveTranslation}
        setShowSubTitle={setShowSubTitle}
        showSubTitle={showSubTitle}
        setYourPreferredLanguage={setYourPreferredLanguage}
        yourPreferredLanguage={yourPreferredLanguage}
        setSelectedDevicePreferredLanguage={setSelectedDevicePreferredLanguage}
        selectedDevicePreferredLanguage={selectedDevicePreferredLanguage}
        messages={messages}
        isTranscriptionEnabled={callData.isTranscriptionEnabled}
        call_id={callData.call_id}
        playCallingTone={callData.playCallingTone}
      />
        <AgoraRTMTranscriptions
          deviceId={callData.callingDeviceId}
          callingAccountId={callData.callingAccountId}
          yourPreferredLanguage={yourPreferredLanguage}
          selectedDevicePreferredLanguage={selectedDevicePreferredLanguage}
          setMessages={setMessages}
          liveTranslation={liveTranslation}
          preferredLanguage={yourPreferredLanguage}
        />
    </div>
  );
}

export default VideoCallPage;